import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmPopover, HlmPopoverImports } from '@spartan-ng/helm/popover';

import { ThemedIconComponent } from '../../../../atoms/themed-icon/themed-icon.component';
import { TableColumnAction, TableColumnConfig } from '../../../table-header/table-header.interface';
import { DropdownCoordinationService } from '../../../dropdown-coordination.service';
import { HlmTd } from '@spartan-ng/helm/table';

export interface ActionMenuItem {
  label: string;
  disabled: boolean;
  command: () => void;
}

@Component({
  selector: 'ocp-table-row-actions',
  imports: [TranslatePipe, HlmPopoverImports, ThemedIconComponent, HlmTd],
  template: `
    <td
      hlmTableCell
      class="table-row-actions__cell"
      [class]="column().cssClass"
      [class.table-row-actions__cell--sticky]="column().sticky"
      [style.width]="column().width"
      [style.min-width]="column().minWidth"
      (click)="$event.stopPropagation()"
    >
      <hlm-popover align="end">
        <button
          hlmPopoverTrigger
          type="button"
          class="table-row-actions__btn"
          [disabled]="disabled()"
          [attr.aria-label]="column().label ? (column().label! | translate) : ''"
          (click)="onTriggerClick()"
        >
          <ocp-themed-icon name="three-dots.svg" [size]="18" />
        </button>
        <hlm-popover-content *hlmPopoverPortal class="w-fit min-w-[220px]">
          <div class="table-row-actions__menu">
            @for (item of menuItems(); track item.label) {
              <button
                type="button"
                class="popover-menu-item"
                [class.popover-menu-item--disabled]="item.disabled"
                [disabled]="item.disabled"
                (click)="onMenuItemClick(item)"
              >
                {{ item.label }}
              </button>
            }
          </div>
        </hlm-popover-content>
      </hlm-popover>
    </td>
  `,
  styles: `
    :host {
      display: contents;
    }

    .table-row-actions__cell {
      text-align: end;
      padding-inline-end: 0;
    }

    .table-row-actions__cell--sticky {
      position: sticky;
      right: 0;
      /* --row-background is set by .table-row on hover/selected (see table-row.component.scss)
         so this cell's background follows the row's state instead of always being flat/static. */
      background-color: var(--row-background, var(--corporate-surface-card));
      /* CSS custom properties don't interpolate, so without its own transition this cell's
         background would snap immediately instead of fading like the row's transition-colors
         (150ms cubic-bezier(0.4, 0, 0.2, 1) — Tailwind's default), causing a visible mismatch. */
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
      /* This cell's own opaque background paints over the row's bottom border (a box-shadow
         on .table-row), so it's redrawn here on top of the cell's own background. */
      box-shadow:
        -1px 0 0 0 var(--border-primary),
        inset 0 -1px 0 var(--border-neutral-500);
    }

    .table-row-actions__menu {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      width: 100%;
    }

    .table-row-actions__btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.3125rem;
      border-radius: 0.25rem;

      &:hover {
        background-color: var(--surface-brand-primary-hover);
      }

      &:focus-visible {
        outline: 2px solid var(--corporate-focus-ring);
      }
    }

    .popover-menu-item {
      display: flex;
      align-items: center;
      width: 100%;
      padding: var(--spacing-4);
      border: none;
      background: none;
      cursor: pointer;
      font-size: var(--font-size-sm);
      color: var(--corporate-text-primary);
      border-radius: var(--radius-xs);
      white-space: nowrap;
      text-align: left;

      &:hover {
        background-color: var(--surface-brand-primary-hover);
      }

      &:focus-visible {
        outline: none;
        background-color: var(--surface-brand-primary-hover);
        box-shadow: inset 0 0 0 2px var(--corporate-focus-ring);
      }
    }

    .popover-menu-item--disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .popover-menu-item--disabled:hover,
    .popover-menu-item--disabled:focus-visible {
      background-color: transparent;
      box-shadow: none;
    }
  `,
})
export class TableRowActionsComponent {
  private readonly dropdownCoordination = inject(DropdownCoordinationService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly popover = viewChild.required(HlmPopover);

  readonly column = input.required<TableColumnConfig>();
  readonly rowData = input.required<unknown>();
  readonly disabled = input(false);

  constructor() {
    this.dropdownCoordination.registerCloseHandler(
      this.elementRef,
      () => this.closePopover(),
      this.destroyRef
    );
  }

  private closePopover(): void {
    this.popover().close();
  }

  protected onTriggerClick(): void {
    if (this.disabled()) return;
    this.dropdownCoordination.notifyOpen(this.elementRef);
  }

  protected onMenuItemClick(item: ActionMenuItem): void {
    if (item.disabled) return;
    // Close first: commands that open a dialog would otherwise leave the popover stuck open.
    this.closePopover();
    item.command();
  }

  protected readonly menuItems = computed<ActionMenuItem[]>(() => {
    const col = this.column();
    const rowData = this.rowData();
    if (!col.actions) return [];
    const actions: TableColumnAction[] =
      typeof col.actions === 'function' ? col.actions(rowData) : col.actions;
    return actions.map(action => ({
      label: action.label,
      disabled:
        typeof action.disabled === 'function' ? action.disabled(rowData) : !!action.disabled,
      command: () => action.command(rowData),
    }));
  });
}
